import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import axios from 'src/utils/axios';
import PropTypes from 'prop-types';
import Collapse from '@mui/material/Collapse';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Autocomplete from '@mui/material/Autocomplete';
import BagliUrunAutocomplete from './BagliUrunAutocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptsCancelButton from './ReceiptCancelButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Modern stil tanımları
const styles = {
  tableContainer: {
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
  },
  tableHead: {
    backgroundColor: '#f8fafc',
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: '#475569',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      padding: '16px',
      borderBottom: '2px solid #e2e8f0',
    },
  },
  parentRow: {
    backgroundColor: '#ffffff',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
    '& > *': { 
      borderBottom: '1px solid #f1f5f9',
    },
  },
  expandButton: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#e2e8f0',
    },
  },
  detailRow: {
    backgroundColor: '#fafafa',
  },
  detailContainer: {
    margin: '16px',
    padding: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
  },
  detailTitle: {
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '1rem',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  innerTableHead: {
    backgroundColor: '#f1f5f9',
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: '#64748b',
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      padding: '12px 16px',
    },
  },
  innerTableRow: {
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#fafafa',
    },
  },
  innerTableCell: {
    padding: '12px 16px',
    fontSize: '0.875rem',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
  },
  statusChipSuccess: {
    backgroundColor: '#dcfce7',
    color: '#166534',
    fontWeight: 600,
    fontSize: '0.75rem',
    borderRadius: '6px',
    border: '1px solid #bbf7d0',
  },
  statusChipError: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    fontWeight: 600,
    fontSize: '0.75rem',
    borderRadius: '6px',
    border: '1px solid #fecaca',
  },
  countChip: {
    fontWeight: 600,
    fontSize: '0.8rem',
    minWidth: '32px',
    borderRadius: '6px',
  },
  closedCountChip: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  openCountChip: {
    backgroundColor: '#fef3c7',
    color: '#b45309',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontSize: '0.875rem',
      minHeight: '40px',
      height: 'auto',
      '& .MuiOutlinedInput-input': {
        padding: '8px 14px',
        height: 'auto',
        lineHeight: '1.5',
        display: 'flex',
        alignItems: 'center',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#cbd5e1',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#3b82f6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
      },
    },
    '& .MuiInputLabel-root': {
      lineHeight: '1.2',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
      '&:not(.MuiInputLabel-shrink)': {
        transform: 'translate(14px, 10px) scale(1)',
      },
    },
    '& .MuiAutocomplete-inputRoot': {
      padding: '0 9px !important',
      '& .MuiAutocomplete-input': {
        padding: '8px 4px !important',
      },
    },
  },
  autocomplete: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontSize: '0.875rem',
      minHeight: '40px',
      height: 'auto',
      '& .MuiOutlinedInput-input': {
        padding: '8px 14px',
        height: 'auto',
        lineHeight: '1.5',
      },
    },
    '& .MuiInputLabel-root': {
      lineHeight: '1.2',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
      '&:not(.MuiInputLabel-shrink)': {
        transform: 'translate(14px, 10px) scale(1)',
      },
    },
    '& .MuiAutocomplete-inputRoot': {
      padding: '0 9px !important',
      '& .MuiAutocomplete-input': {
        padding: '8px 4px !important',
      },
    },
  },
  bagliUrunColumn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '4px 0',
  },
  technicianColumn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '4px 0',
  },
  lgmvIcon: {
    width: 28,
    height: 28,
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
  },
};

function createData(line, rows) {
  if (line.Type === 18) {
    return {
      systemName: line.SystemName,
      closedReceiptsCount: rows.filter(
        x => (x.BarcodeControl === 1 || x.AcikKapali === 1) && x.Type === 18
      ).length,
      openReceiptscount: rows.filter(
        x => (x.BarcodeControl === 0 || x.AcikKapali === 0) && x.Type === 18
      ).length,
      lgmvFieldUpload: line.lgmvFieldUpload > 0 ? 1 : 0,
      type: line.Type,
      history: rows
        .filter(x => x.Type === 18)
        .map(item => ({
          Id: item.Id,
          SystemName: item.SystemName,
          ModelCode: item.ModelCode,
          ModelDesc: item.ModelDesc,
          FisNo: item.FisNo,
          ReceiptStatusExplanation: item.ReceiptStatusExplanation,
          BagliUrun: item.BagliUrun,
          TechnicianId: item.TechnicianId,
          Barcode: item.Barcode,
          StatusCompleted: item.StatusCompleted,
          BarcodeControl: item.BarcodeControl,
          Type: item.Type,
          DogrulamaNotes: item.DogrulamaNotes,
          BarcodeControl: item.BarcodeControl,
          TechnicianControl: item.TechnicianControl,
          BardcodeBindingDate: item.BardcodeBindingDate,
          AcikKapali: item.AcikKapali
        }))
    };
  }
  if (line.Type === 16) {
    return {
      systemName: line.SystemName,
      closedReceiptsCount: rows.filter(
        x => (x.BarcodeControl === 1 || x.AcikKapali === 1) && x.Type === 16
      ).length,
      openReceiptscount: rows.filter(
        x => (x.BarcodeControl === 0 || x.AcikKapali === 0) && x.Type === 16
      ).length,
      lgmvFieldUpload: line.lgmvFieldUpload > 0 ? 1 : 0,
      type: line.Type,
      history: rows
        .filter(x => x.Type === 16)
        .map(item => ({
          Id: item.Id,
          SystemName: item.SystemName,
          ModelCode: item.ModelCode,
          ModelDesc: item.ModelDesc,
          FisNo: item.FisNo,
          ReceiptStatusExplanation: item.ReceiptStatusExplanation,
          BagliUrun: item.BagliUrun,
          TechnicianId: item.TechnicianId,
          Barcode: item.Barcode,
          StatusCompleted: item.StatusCompleted,
          BarcodeControl: item.BarcodeControl,
          Type: item.Type,
          DogrulamaNotes: item.DogrulamaNotes,
          BarcodeControl: item.BarcodeControl,
          TechnicianControl: item.TechnicianControl,
          BardcodeBindingDate: item.BardcodeBindingDate,
          AcikKapali: item.AcikKapali
        }))
    };
  }
  if (line.Type === 17) {
    return {
      systemName: line.SystemName,
      closedReceiptsCount: rows.filter(
        x => (x.BarcodeControl === 1 || x.AcikKapali === 1) && x.Type === 17
      ).length,
      openReceiptscount: rows.filter(
        x => (x.BarcodeControl === 0 || x.AcikKapali === 0) && x.Type === 17
      ).length,
      lgmvFieldUpload: line.lgmvFieldUpload > 0 ? 1 : 0,
      type: line.Type,
      history: rows
        .filter(x => x.Type === 17)
        .map(item => ({
          Id: item.Id,
          SystemName: item.SystemName,
          ModelCode: item.ModelCode,
          ModelDesc: item.ModelDesc,
          FisNo: item.FisNo,
          ReceiptStatusExplanation: item.ReceiptStatusExplanation,
          BagliUrun: item.BagliUrun,
          TechnicianId: item.TechnicianId,
          Barcode: item.Barcode,
          StatusCompleted: item.StatusCompleted,
          BarcodeControl: item.BarcodeControl,
          Type: item.Type,
          DogrulamaNotes: item.DogrulamaNotes,
          BarcodeControl: item.BarcodeControl,
          TechnicianControl: item.TechnicianControl,
          BardcodeBindingDate: item.BardcodeBindingDate,
          AcikKapali: item.AcikKapali
        }))
    };
  }
  if (line.Type === 15) {
    return {
      systemName: line.SystemName,
      closedReceiptsCount: rows.filter(
        x => (x.BarcodeControl === 1 || x.AcikKapali === 1) && x.Type === 15
      ).length,
      openReceiptscount: rows.filter(
        x => (x.BarcodeControl === 0 || x.AcikKapali === 0) && x.Type === 15
      ).length,
      lgmvFieldUpload: line.lgmvFieldUpload > 0 ? 1 : 0,
      type: line.Type,
      history: rows
        .filter(x => x.Type === 15)
        .map(item => ({
          Id: item.Id,
          SystemName: item.SystemName,
          ModelCode: item.ModelCode,
          ModelDesc: item.ModelDesc,
          FisNo: item.FisNo,
          ReceiptStatusExplanation: item.ReceiptStatusExplanation,
          BagliUrun: item.BagliUrun,
          TechnicianId: item.TechnicianId,
          Barcode: item.Barcode,
          StatusCompleted: item.StatusCompleted,
          BarcodeControl: item.BarcodeControl,
          Type: item.Type,
          DogrulamaNotes: item.DogrulamaNotes,
          BarcodeControl: item.BarcodeControl,
          TechnicianControl: item.TechnicianControl,
          BardcodeBindingDate: item.BardcodeBindingDate,
          AcikKapali: item.AcikKapali
        }))
    };
  }
  if (line.Type === 0) {
    return {
      systemName: line.SystemName,
      closedReceiptsCount: rows.filter(
        x => (x.BarcodeControl === 1 || x.AcikKapali === 1) && x.BindedSystemId === line.DetailId
      ).length,
      openReceiptscount: rows.filter(
        x => (x.BarcodeControl === 0 || x.AcikKapali === 0) && x.BindedSystemId === line.DetailId
      ).length,
      lgmvFieldUpload: line.lgmvFieldUpload > 0 ? 1 : 0,
      type: line.Type,
      history: rows
        .filter(x => x.BindedSystemId === line.DetailId)
        .map(item => ({
          Id: item.Id,
          SystemName: item.SystemName,
          ModelCode: item.ModelCode,
          ModelDesc: item.ModelDesc,
          FisNo: item.FisNo,
          ReceiptStatusExplanation: item.ReceiptStatusExplanation,
          BagliUrun: item.BagliUrun,
          TechnicianId: item.TechnicianId,
          Barcode: item.Barcode,
          StatusCompleted: item.StatusCompleted,
          BarcodeControl: item.BarcodeControl,
          Type: item.Type,
          DogrulamaNotes: item.DogrulamaNotes,
          BarcodeControl: item.BarcodeControl,
          TechnicianControl: item.TechnicianControl,
          BardcodeBindingDate: item.BardcodeBindingDate,
          AcikKapali: item.AcikKapali
        }))
    };
  }
}

function Row(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [loading, setLoading] = React.useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const isMobile = false; //useMediaQuery('(max-width:600px)');
  const { row } = props;
  const { data } = props;
  const { setData } = props;
  const { bagliUrunData } = props;
  const { setBagliUrunData } = props;
  const { errors } = props;
  const { setErrors } = props;
  const { errorsArray } = props;
  const { setErrorsArray } = props;
  const { selectedRow } = props;
  const { fetchHavcProjectReceipts } = props;
  const { cancelledReceipts } = props;
  const { setCancelledReceipts } = props;
  const [open, setOpen] = React.useState(false);
  const [activeRowFisNo, setActiveRowFisNo] = useState(null);
  const [errorFisNo, setErrorFisNo] = useState(null);

  // Dış Ünite + LGMV kontrolü: LGMV yoksa bireysel barkod kaydını engelle
  const isDısUniteWithoutLgmv = (rowId) => {
    if (row.type !== 0 || row.lgmvFieldUpload > 0) return false;
    const detailRow = row.history.find(x => x.Id === rowId);
    const name = (detailRow?.SystemName || '').toLowerCase();
    return name.includes('dış ünite') || name.includes('dış unite') || name.includes('dis ünite') || name.includes('dis unite');
  };

  const handleChange = (newValue, rowId) => {
    if (newValue) {
      const updatedSystem = data.Receipts.find(row => row.Id === rowId)
        ?.BindedSystemId;

      setData(prevData => ({
        ...prevData,
        Receipts: prevData.Receipts.map(row =>
          row.BindedSystemId === updatedSystem && row.TechnicianId === 0
            ? { ...row, TechnicianId: newValue?.Id ? newValue?.Id : 0 }
            : row
        )
      }));
    }

    setData(prevData => ({
      ...prevData,
      Receipts: prevData?.Receipts?.map(row =>
        row.Id === rowId
          ? { ...row, TechnicianId: newValue?.Id ? newValue?.Id : 0 }
          : row
      )
    }));
  };

  const handleBarcodeBlur = (event, rowId) => {
    const newValue = event.target.value;

    setData(prevData => ({
      ...prevData,
      Receipts: prevData?.Receipts?.map(row =>
        row.Id === rowId ? { ...row, Barcode: newValue } : row
      )
    }));

    setErrors(prevErrors => {
      const fisNo = data?.Receipts?.find(row => row.Id === rowId)?.FisNo;
      const barcodeKey = `Barcode${fisNo}`;

      if (newValue === '') {
        const { [barcodeKey]: _, ...remainingErrors } = prevErrors;
        setErrorFisNo(-1);
        return remainingErrors;
      }
      return prevErrors;
    });
  };

  const handleRenewClick = (barcode, rowId, FisNo, TechnicianId) => {
    if (isDısUniteWithoutLgmv(rowId)) return;
    setErrorFisNo(null);
    setActiveRowFisNo(FisNo);
    callLineBarcodeApi(barcode, rowId, FisNo, TechnicianId);
  };

  const handleBarcodeChange = (event, rowId, FisNo, TechnicianId) => {
    const barcode = event.target.value;

    if (barcode.length > 21) {
      // LGMV yoksa Dış Ünite barkodunu otomatik kaydetme
      if (isDısUniteWithoutLgmv(rowId)) return;
      setActiveRowFisNo(FisNo);
      callLineBarcodeApi(barcode, rowId, FisNo, TechnicianId);
      // const nextRowId = getNextRowId(rowId);
      // if (nextRowId) {
      //   const nextField = document.querySelector(`input#Barcode-${nextRowId}`);
      //   if (nextField) {
      //     nextField.focus();
      //   }
      // }
    }
  };

  const callLineBarcodeApi = (barcode, rowId, FisNo, TechnicianId) => {
    setErrors([]);
    axios
      .post(
        `SetHavcSingleLineBarcode/${selectedRow.ProjectID}/${FisNo}/${barcode}/${TechnicianId}`
      )
      .then(response => {
        if (response.data.includes('Error:')) {
          setErrors({
            [`Barcode${FisNo}`]: response.data.split('Error:')[1]
          });
          setActiveRowFisNo(null);
          setErrorFisNo(FisNo);
        } else {
          const diffs = [];
          diffs.push({
            oldValue: null,
            newValue: barcode,
            key: 'Barkod',
            id: null, //selectedRow.BindedSystemId == 0 ? null : selectedRow.BindedSystemId,
            ProjectID: selectedRow.ProjectID,
            fisNo: FisNo
          })
          axios.post('SetHavcProjectLog', diffs
            , {headers: { 'Content-Type': 'application/json' }});
          const dataCopy = [...data.Receipts];
          const technicianMap = new Map(
            dataCopy.map(receipt => [receipt.Id, receipt.TechnicianId])
          );
          fetchHavcProjectReceipts(selectedRow.ProjectID).then(() => {
            setData(prevData => ({
              ...prevData,
              Receipts: prevData.Receipts.map(receipt => ({
                ...receipt,
                TechnicianId:
                  receipt.FisNo > 0
                    ? technicianMap.get(receipt.Id) || receipt.TechnicianId
                    : receipt.TechnicianId
              }))
            }));
          });
          const nextRowId = getNextRowId(rowId);
          if (nextRowId) {
            const nextField = document.querySelector(
              `input#Barcode-${nextRowId}`
            );
            if (nextField) {
              nextField.focus();
            }
          }
          setActiveRowFisNo(null);
        }
      })
      .catch(error => {
        setActiveRowFisNo(null);
      })
      .finally(() => {
        setActiveRowFisNo(null);
      });
  };

  const getNextRowId = currentRowId => {
    return data?.Receipts.find(
      row => row.Id > currentRowId && row.FisNo > 0 && row.Barcode === ''
    ).Id;
  };

  return (
    <React.Fragment>
      <TableRow
        sx={styles.parentRow}
        onClick={() => setOpen(!open)}
      >
        <TableCell sx={{ width: 60 }}>
          <IconButton
            aria-label="expand row"
            size="small"
            sx={styles.expandButton}
          >
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography sx={{ fontWeight: 600, color: '#1e293b' }}>
            {row.systemName}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Chip 
            label={row.closedReceiptsCount} 
            size="small" 
            sx={{ ...styles.countChip, ...styles.closedCountChip }}
          />
        </TableCell>
        <TableCell align="center">
          <Chip 
            label={row.openReceiptscount} 
            size="small" 
            sx={{ ...styles.countChip, ...styles.openCountChip }}
          />
        </TableCell>
        <TableCell align="center">
          {row.type === 0 && (
            <Box sx={{ 
              ...styles.lgmvIcon, 
              backgroundColor: row.lgmvFieldUpload > 0 ? '#dcfce7' : '#fef2f2' 
            }}>
              {row.lgmvFieldUpload > 0 ? (
                <CheckCircleIcon sx={{ color: '#16a34a', fontSize: '20px' }} />
              ) : (
                <CancelIcon sx={{ color: '#dc2626', fontSize: '20px' }} />
              )}
            </Box>
          )}
        </TableCell>
      </TableRow>
      <TableRow sx={styles.detailRow}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={styles.detailContainer}>
              <Typography sx={styles.detailTitle}>
                📋 Fiş Detayları
                <Chip 
                  label={`${row.history.length} kayıt`} 
                  size="small" 
                  sx={{ backgroundColor: '#f1f5f9', color: '#64748b', fontSize: '0.75rem' }}
                />
              </Typography>
              <TableContainer sx={{ borderRadius: '8px', border: '1px solid #e2e8f0', overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead sx={styles.innerTableHead}>
                    <TableRow>
                      <TableCell>Sistem Adı</TableCell>
                      <TableCell>Model Kod</TableCell>
                      <TableCell>Model Açıklama</TableCell>
                      <TableCell>Fiş No</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Bağlı Ürünler</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>Teknisyen</TableCell>
                      <TableCell sx={{ minWidth: 320 }}>Ürün Barkodu</TableCell>
                      <TableCell align="center">İşlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.history.map(detailRow => (
                      <TableRow key={detailRow.FisNo} sx={styles.innerTableRow}>
                        <TableCell sx={styles.innerTableCell}>
                          <Typography sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                            {detailRow.SystemName}
                          </Typography>
                        </TableCell>
                        <TableCell sx={styles.innerTableCell}>
                          <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
                            {detailRow.ModelCode}
                          </Typography>
                        </TableCell>
                        <TableCell sx={styles.innerTableCell}>
                          <Typography sx={{ fontSize: '0.85rem' }}>
                            {detailRow.ModelDesc}
                          </Typography>
                        </TableCell>
                        <TableCell sx={styles.innerTableCell}>
                          <a
                            href={`https://aron.arcelik.com/Main/Home/${detailRow.FisNo}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                          >
                            <Chip
                              label={detailRow.FisNo}
                              size="small"
                              sx={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: '0.75rem' }}
                            />
                          </a>
                        </TableCell>
                        <TableCell sx={styles.innerTableCell}>
                          <Tooltip title={detailRow.DogrulamaNotes || ''} arrow>
                            <Chip
                              label={detailRow.ReceiptStatusExplanation}
                              size="small"
                              sx={detailRow.StatusCompleted ? styles.statusChipSuccess : styles.statusChipError}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={styles.innerTableCell}>
                          {(detailRow.Type === 16 || detailRow.Type === 17) && (
                            <Box sx={styles.bagliUrunColumn}>
                              <BagliUrunAutocomplete
                                data={data}
                                type={detailRow.Type}
                                fisNo={detailRow.FisNo}
                                bagliUrunData={bagliUrunData}
                                setBagliUrunData={setBagliUrunData}
                              />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={styles.innerTableCell}>
                          <Box sx={styles.technicianColumn}>
                            <Autocomplete
                              id="TechnicianId"
                              name="TechnicianId"
                              fullWidth
                              size="small"
                              sx={styles.autocomplete}
                              disabled={
                                detailRow.TechnicianControl === 1 ||
                                cancelledReceipts.filter(
                                  x => x.FisNo === detailRow.FisNo
                                ).length
                                || detailRow.AcikKapali === 1
                              }
                              options={data.Technicians ?? []}
                              getOptionLabel={teknisyen =>
                                teknisyen.Dsc ? teknisyen.Dsc : ''
                              }
                              value={
                                data?.Technicians?.find(
                                  x =>
                                    x.Id ===
                                    data.Receipts.find(x => x.Id === detailRow.Id)
                                      .TechnicianId
                                ) || null
                              }
                              onChange={(event, newValue) =>
                                handleChange(newValue, detailRow.Id)
                              }
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  label="Sorumlu Teknisyen"
                                  variant="outlined"
                                  size="small"
                                  error={Boolean(
                                    errors[`TechnicianId${detailRow.FisNo}`]
                                  )}
                                  helperText={
                                    errors[`TechnicianId${detailRow.FisNo}`]
                                  }
                                  sx={styles.textField}
                                />
                              )}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={styles.innerTableCell}>
                          <Grid container alignItems="center" spacing={1} wrap="nowrap">
                            <Grid item xs>
                              <TextField
                                id={`Barcode-${detailRow.Id}`}
                                name="Barcode"
                                label="Barkod"
                                placeholder="Ürün Barkodu"
                                variant="outlined"
                                size="small"
                                sx={{
                                  ...styles.textField,
                                  '& .MuiOutlinedInput-root': {
                                    ...styles.textField['& .MuiOutlinedInput-root'],
                                    fontSize: '0.78rem',
                                    '& .MuiOutlinedInput-input': {
                                      padding: '8px 8px',
                                      height: 'auto',
                                      lineHeight: '1.5',
                                      display: 'flex',
                                      alignItems: 'center',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    },
                                  },
                                }}
                                error={Boolean(
                                  errors[`Barcode${detailRow.FisNo}`]
                                )}
                                helperText={errors[`Barcode${detailRow.FisNo}`]}
                                fullWidth
                                defaultValue={detailRow.Barcode || ''}
                                disabled={
                                  detailRow.BarcodeControl === 1 ||
                                  cancelledReceipts.filter(
                                    x => x.FisNo === detailRow.FisNo
                                  ).length
                                  || detailRow.AcikKapali === 1
                                }
                                onBlur={event =>
                                  handleBarcodeBlur(event, detailRow.Id)
                                }
                                title={detailRow?.BardcodeBindingDate}
                                onChange={event =>
                                  handleBarcodeChange(
                                    event,
                                    detailRow.Id,
                                    detailRow.FisNo,
                                    data.Receipts.find(
                                      x => x.Id === detailRow.Id
                                    ).TechnicianId
                                  )
                                }
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {errorFisNo === detailRow.FisNo ? (
                                        <IconButton
                                          size="small"
                                          sx={{ color: '#3b82f6' }}
                                          onClick={event =>
                                            handleRenewClick(
                                              detailRow.Barcode,
                                              detailRow.Id,
                                              detailRow.FisNo,
                                              data.Receipts.find(
                                                x => x.Id === detailRow.Id
                                              ).TechnicianId
                                            )
                                          }
                                        >
                                          <RefreshIcon fontSize="small" />
                                        </IconButton>
                                      ) : (
                                        activeRowFisNo === detailRow.FisNo && (
                                          <CircularProgress size={18} thickness={4} />
                                        )
                                      )}
                                    </InputAdornment>
                                  )
                                }}
                              />
                            </Grid>
                            <Grid item xs="auto">
                              {((detailRow.Barcode > 0 &&
                                Array.isArray(errors) &&
                                !errors.some(item =>
                                  item.hasOwnProperty(
                                    `Barcode${detailRow.FisNo}`
                                  )
                                ) &&
                                errorFisNo !== detailRow.FisNo &&
                                activeRowFisNo !== detailRow.FisNo) ||
                                detailRow.BarcodeControl === 1) && (
                                <CheckCircleIcon sx={{ color: '#16a34a', fontSize: '22px' }} />
                              )}
                            </Grid>
                          </Grid>
                        </TableCell>
                        <TableCell sx={styles.innerTableCell} align="center">
                          <ReceiptsCancelButton
                            data={data}
                            setData={setData}
                            FisNo={detailRow.FisNo}
                            cancelledReceipts={cancelledReceipts}
                            setCancelledReceipts={setCancelledReceipts}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function ReceiptCollapsibleTable({
  data,
  setData,
  bagliUrunData,
  setBagliUrunData,
  errors,
  setErrors,
  errorsArray,
  setErrorsArray,
  selectedRow,
  fetchHavcProjectReceipts,
  cancelledReceipts,
  setCancelledReceipts
}) {
  const groupedArray = data?.Receipts?.filter(item => item.Level === 1)?.reduce(
    (acc, item) => {
      const groupKey = item.Type === 0 ? item.DetailId : item.Type;
      if (!acc[groupKey]) {
        acc[groupKey] = item;
      }
      return acc;
    },
    {}
  );

  const resultArray = Object.values(groupedArray || {}).sort(
    (a, b) => b.Type - a.Type
  );

  const rows = resultArray?.map(item => createData(item, data?.Receipts));

  if (!rows || rows.length === 0) {
    return (
      <Paper sx={{ ...styles.tableContainer, padding: '48px', textAlign: 'center' }}>
        <Typography sx={{ color: '#64748b', fontSize: '1rem' }}>
          📋 Gösterilecek fiş bulunmuyor
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} sx={styles.tableContainer}>
      <Table aria-label="collapsible table">
        <TableHead sx={styles.tableHead}>
          <TableRow>
            <TableCell sx={{ width: 60 }} />
            <TableCell>Tanım</TableCell>
            <TableCell align="center">Kapalı Fiş</TableCell>
            <TableCell align="center">Açık Fiş</TableCell>
            <TableCell align="center">LGMV Yüklemesi</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.map((row, index) => (
            <Row
              key={`${row.systemName}-${index}`}
              row={row}
              data={data}
              setData={setData}
              bagliUrunData={bagliUrunData}
              setBagliUrunData={setBagliUrunData}
              errors={errors}
              setErrors={setErrors}
              errorsArray={errorsArray}
              setErrorsArray={setErrorsArray}
              selectedRow={selectedRow}
              fetchHavcProjectReceipts={fetchHavcProjectReceipts}
              cancelledReceipts={cancelledReceipts}
              setCancelledReceipts={setCancelledReceipts}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
